import React, { useState } from 'react';
import { Calendar as CalendarIcon, Heart, Calculator, Download, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { cn } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CycleCalculator = () => {
  const [lastPeriodDate, setLastPeriodDate] = useState('');
  const [cycleLength, setCycleLength] = useState(28);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    if (!lastPeriodDate) {
      toast.error('Please enter your last period date');
      return;
    }

    // Backend expects yyyy-mm-dd format ✅
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(lastPeriodDate)) {
      toast.error('Invalid date format. Please use YYYY-MM-DD.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/cycle/calculate`, {
        last_period_date: lastPeriodDate,
        cycle_length: cycleLength
      });
      setResult(response.data);
      toast.success('Cycle calculated successfully!');
    } catch (error) {
      console.error('Error calculating cycle:', error);
      toast.error('Failed to calculate cycle');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <Card className="p-4 md:p-8 border-pink-100 shadow-xl">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] rounded-full flex items-center justify-center">
          <Calculator className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Free Cycle Calculator</h2>
        <p className="text-gray-600">Get accurate predictions for your next period and ovulation</p>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <Label htmlFor="last-period">Last Period Start Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="last-period"
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal mt-2 h-12 bg-white border-gray-200",
                  !lastPeriodDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-[#FF6B9D]" />
                {lastPeriodDate ? (
                  <span className="text-gray-900">{lastPeriodDate}</span>
                ) : (
                  <span className="text-gray-400">YYYY-MM-DD</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-pink-100" align="start">
              <Calendar
                mode="single"
                selected={lastPeriodDate ? new Date(lastPeriodDate) : undefined}
                onSelect={(date) => setLastPeriodDate(date ? format(date, "yyyy-MM-dd") : '')}
                disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                initialFocus
                className="rounded-md border-pink-50"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <Label htmlFor="cycle-length">Average Cycle Length (days)</Label>
          <Input
            id="cycle-length"
            type="number"
            value={cycleLength}
            onChange={(e) => setCycleLength(Number(e.target.value))}
            min="21"
            max="40"
            className="mt-2"
          />
          <p className="text-sm text-gray-500 mt-1">Typical range: 21-35 days</p>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white"
          disabled={loading}
        >
          {loading ? 'Calculating...' : 'Calculate My Cycle'}
        </Button>
      </div>

      {result && (
        <div className="mt-8">
          <div className="p-6 bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Heart className="w-6 h-6 mr-2 text-[#FF6B9D]" />
              Your Cycle Predictions
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl">
                <p className="text-sm text-gray-600 mb-1">Next Period</p>
                <p className="text-lg font-bold text-[#FF6B9D]">{formatDate(result.next_period_date)}</p>
              </div>
              <div className="bg-white p-4 rounded-xl">
                <p className="text-sm text-gray-600 mb-1">Ovulation</p>
                <p className="text-lg font-bold text-[#FF6B9D]">{formatDate(result.ovulation_date)}</p>
              </div>
              {result.fertile_window_start && (
                <>
                  <div className="bg-white p-4 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Fertile Window Start</p>
                    <p className="text-lg font-bold text-purple-600">{formatDate(result.fertile_window_start)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Fertile Window End</p>
                    <p className="text-lg font-bold text-purple-600">{formatDate(result.fertile_window_end)}</p>
                  </div>
                </>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center">
              Based on your average cycle length of {result.cycle_length} days
            </p>
          </div>

          {/* Download App CTA */}
          <div className="bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] p-6 md:p-10 rounded-2xl text-center text-white">
            <Smartphone className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Want to Track Your Cycle?</h3>
            <p className="text-white/90 mb-6">
              Download Ovalu to save your data, get AI-powered insights, personalized health plans,
              and never miss your period again!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center">
              <Button
                onClick={() => window.open('https://play.google.com/store', '_blank')}
                className="bg-white text-[#FF6B9D] hover:bg-gray-50 rounded-full px-8 py-6 text-lg font-semibold w-full sm:w-[250px]"
              >
                <Download className="w-5 h-5 mr-2" />
                Download for Android
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default CycleCalculator;
